import React, { useEffect, useMemo, useRef, useState } from 'react';
import { List } from 'immutable';
import moment from 'moment';

import { fetchOlderWidgetMessages } from 'flow/actions/channels-actions';
import WidgetMessageItem from 'flow/components/message/PbxWidgetMessageItem';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import MessageGroupDivider from 'flow/pages/messages/MessageGroupDivider';
import { WidgetMessage } from 'flow/types/Channel';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { groupListBy } from 'flow/utils';
import {
    animatedScrollTo,
    moveScrollTo,
    scrollAtBottom,
    scrollAtTop,
} from 'flow/utils/element-utils';
import {
    getMessageGroups,
    lastMessageSentByMe,
    receivedNewMessages,
    receivedOldMessages,
} from 'flow/utils/message-utils';

interface Props {
    messages: List<TypedMap<WidgetMessage>>;
    chatSessionKey: ChatSessionKey;
}

const PbxChannelMessages = ({ chatSessionKey, messages }: Props) => {
    const [isScrollAtBottom, setIsScrollAtBottom] = useState(true);
    const [isScrollAtTop, setIsScrollAtTop] = useState(false);

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const dispatch = useAppDispatch();

    const prevMessagesRef = useRef(messages);
    const scrollTopRef = useRef(0);
    const scrollHeightRef = useRef(0);
    const isScrollAtBottomRef = useRef(isScrollAtBottom);

    const widgetMessagesElement = useRef<HTMLDivElement>(null);

    const detectScrollPosition = () => {
        const listNode = widgetMessagesElement.current;
        if (listNode) {
            setIsScrollAtBottom(scrollAtBottom(listNode));
            setIsScrollAtTop(scrollAtTop(listNode));
            scrollTopRef.current = listNode.scrollTop;
            scrollHeightRef.current = listNode.scrollHeight;
        }
    };

    const handleScrollToBottom = () => {
        const node = widgetMessagesElement.current;
        if (!node) return;

        moveScrollTo(node, node.scrollHeight);
    };

    const handleAnimatedScrollToBottom = () => {
        const node = widgetMessagesElement.current;
        if (!node) return;

        const moveTo = node.scrollHeight - node.clientHeight;

        animatedScrollTo(node, moveTo);
    };

    const handleKeepScrollPosition = (
        scrollTop: number,
        scrollHeight: number,
    ) => {
        const node = widgetMessagesElement.current;
        if (!node) return;

        const constantBottomDiff = scrollHeight - scrollTop;
        moveScrollTo(node, node.scrollHeight - constantBottomDiff);
    };

    useEffect(() => {
        widgetMessagesElement.current.onscroll = detectScrollPosition;
        handleScrollToBottom();
    }, []);

    useEffect(() => {
        const oldMessages = prevMessagesRef.current;
        const newMessages = messages;

        const noNewMessages = oldMessages.size === newMessages.size;
        const newMessageAndScrollAtBottom =
            receivedNewMessages(oldMessages, newMessages) &&
            isScrollAtBottomRef.current;
        const newMessageAndSentByMe =
            receivedNewMessages(oldMessages, newMessages) &&
            lastMessageSentByMe(newMessages, ownExtensionKey);

        if (isScrollAtTop && noNewMessages) {
            dispatch(
                fetchOlderWidgetMessages(
                    chatSessionKey,
                    messages.getIn([0, 'key']),
                    'contact',
                ),
            );
        }

        if (newMessageAndScrollAtBottom || newMessageAndSentByMe) {
            handleAnimatedScrollToBottom();
        } else if (receivedOldMessages(oldMessages, newMessages)) {
            handleKeepScrollPosition(
                scrollTopRef.current,
                scrollHeightRef.current,
            );
        }
        isScrollAtBottomRef.current = isScrollAtBottom;
        prevMessagesRef.current = messages;
    }, [
        chatSessionKey,
        dispatch,
        isScrollAtBottom,
        isScrollAtTop,
        messages,
        ownExtensionKey,
    ]);

    const renderMessages = useMemo(() => {
        const groupedByDate = groupListBy(
            messages,
            (message: TypedMap<WidgetMessage>) =>
                moment(message.get('sent')).format('YYYY-MM-DD'),
        );
        const sortedDateKeys = groupedByDate.keySeq().sort();
        const groupedMessages = getMessageGroups(groupedByDate);

        return sortedDateKeys.map((date: string) => (
            <div key={date}>
                <MessageGroupDivider date={date} />
                {groupedMessages
                    .get(date)
                    .map((messages: List<TypedMap<WidgetMessage>>) => {
                        const firstMessage = messages.first();
                        return (
                            <WidgetMessageItem
                                key={firstMessage.get('key')}
                                messageList={messages}
                                chatSessionKey={chatSessionKey}
                                extensionKey={ownExtensionKey}
                            />
                        );
                    })}
            </div>
        ));
    }, [messages, chatSessionKey, ownExtensionKey]);

    return (
        <div className="channel-chat-area flex grow flex-col flex-nowrap overflow-hidden dark:bg-white">
            <div
                className="scrollbar relative flex flex-[1_99_auto] flex-col p-4 pt-6"
                ref={widgetMessagesElement}
            >
                <div className="mx-auto flex w-full max-w-180 grow flex-col">
                    <div>{renderMessages}</div>
                </div>
            </div>
        </div>
    );
};

export default PbxChannelMessages;
